import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, MapPin, Facebook, Twitter, Instagram, Linkedin, CreditCard, Fingerprint, Zap, ShieldCheck } from "lucide-react";

export const Footer = () => {
  const navigate = useNavigate();

  const scrollToHowItWorks = () => {
    if (window.location.pathname === '/') {
      const element = document.querySelector('[data-testid="how-it-works"]');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector('[data-testid="how-it-works"]');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    }
  };

  return (
    <footer className="bg-navy text-white" data-testid="footer">
      {/* Security Badges Bar */}
      <div className="border-b border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <p className="text-center text-white/50 text-xs font-semibold tracking-wider mb-6">SECURED & VERIFIED BY</p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-white text-sm">Payments</p>
                <p className="text-xs text-white/60">Secured by Stripe</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                <Fingerprint className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-white text-sm">Identity</p>
                <p className="text-xs text-white/60">Verified by Persona</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-white text-sm">Instant Pay</p>
                <p className="text-xs text-white/60">Powered by OnePay</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-2 mb-6" data-testid="footer-logo">
              <img 
                src="https://customer-assets-rejwkqb3.emergentagent.net/wingman/ec88479e-de0b-4b07-83a2-d0fc0e0593e3/attachments/a0930ce5021847efb76339185d8cd461_image.bin" 
                alt="BQL Leads" 
                className="h-10 w-auto brightness-0 invert"
                onError={(e) => { e.target.src = "/bql-logo.png"; }}
              />
            </Link>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              New Jersey&apos;s trusted marketplace for verified home service professionals. Connect with confidence.
            </p>
            <div className="flex gap-3">
              <a href="https://www.facebook.com/bestqualitycleaning1210" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="social-facebook">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="social-twitter">
                <Twitter className="w-4 h-4" />
              </a>
              <a href="https://www.instagram.com/bestqualitycleaning1210" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="social-instagram">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" data-testid="social-linkedin">
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* For Customers */}
          <div>
            <h4 className="font-bold text-lg mb-6">For Customers</h4>
            <p className="text-emerald-400 text-xs font-semibold mb-4 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" />
              100% FREE — No fees ever!
            </p>
            <ul className="space-y-3">
              <li>
                <Link to="/register/customer" className="text-white/70 hover:text-white transition-colors text-sm">
                  Post a Job Free
                </Link>
              </li>
              <li>
                <Link to="/professionals" className="text-white/70 hover:text-white transition-colors text-sm">
                  Find Professionals
                </Link>
              </li>
              <li>
                <button 
                  onClick={scrollToHowItWorks}
                  className="text-white/70 hover:text-white transition-colors text-sm text-left"
                >
                  How It Works
                </button>
              </li>
              <li>
                <Link to="/categories" className="text-white/70 hover:text-white transition-colors text-sm">
                  Browse Categories
                </Link>
              </li>
            </ul>
          </div>

          {/* For Professionals */}
          <div>
            <h4 className="font-bold text-lg mb-6">For Professionals</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/register/professional" className="text-white/70 hover:text-white transition-colors text-sm">
                  Join as Pro
                </Link>
              </li>
              <li>
                <Link to="/pricing" className="text-white/70 hover:text-white transition-colors text-sm">
                  View Pro Plans
                </Link>
              </li>
              <li>
                <a href="#" className="text-white/70 hover:text-white transition-colors text-sm">
                  Success Stories
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-lg mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-gold mt-0.5" />
                <a href="mailto:bestqualityleads@bqleadsprogigs.com" className="text-white/70 hover:text-white text-sm transition-colors">
                  bestqualityleads@bqleadsprogigs.com
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gold mt-0.5" />
                <span className="text-white/70 text-sm">Serving All of New Jersey</span>
              </li>
            </ul>
            
            <div className="mt-6 p-4 bg-white/5 rounded-lg">
              <p className="text-white/90 text-sm font-medium mb-1">Need Help?</p>
              <p className="text-white/60 text-xs">Our support team is available to assist you with any questions.</p>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/10 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-center md:text-left">
            <p className="text-white/50 text-sm">
              © {new Date().getFullYear()} BQL - Best Quality Leads. All rights reserved.
            </p>
            <p className="text-white/40 text-xs mt-1">
              BQL™ is a registered trademark.
            </p>
          </div>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-white/50 hover:text-white text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-white/50 hover:text-white text-sm transition-colors">
              Terms of Service
            </Link>
            <Link to="/cookies" className="text-white/50 hover:text-white text-sm transition-colors">
              Cookie Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
